/**
 * Phase 7.UI.1 — ThemeContext.
 *
 * Single source of truth for the active UI theme. Responsibilities:
 *   1. Initialise from the value already applied by the early-hydration
 *      script in `public/index.html` (so there is no flash of unstyled
 *      content on first paint).
 *   2. Persist user changes to localStorage immediately.
 *   3. When a user is logged in AND their server-stored preference
 *      differs from the local value, reconcile:
 *        - If user.theme_preference is set, prefer it (cross-device
 *          consistency) and update localStorage.
 *        - If user has no stored pref but localStorage has one, push
 *          local → server (so next device sees it).
 *   4. Expose `setTheme(theme)` which: applies to DOM → writes
 *      localStorage → fires off a PATCH /auth/preferences (best-effort,
 *      no UI block).
 *
 * Display-only — never affects auth, billing, valuation, paywall etc.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import {
  applyThemeToDocument, DEFAULT_THEME, isValidTheme, readStoredTheme,
  resolveInitialTheme, writeStoredTheme,
} from "@/lib/theme";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ThemeCtx = createContext(null);

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  // Initial value comes from what is ALREADY on <html data-theme="…">,
  // which the early-hydration script set before React even mounted.
  // Falling back to localStorage / dark covers SSR / edge cases.
  const [theme, setThemeState] = useState(() => {
    if (typeof document !== "undefined") {
      const fromAttr = document.documentElement.getAttribute("data-theme");
      if (isValidTheme(fromAttr)) return fromAttr;
    }
    return resolveInitialTheme();
  });
  const hasSyncedUserPrefRef = useRef(false);

  /**
   * Public setter — used by the user-facing toggle in the navbar.
   * Updates DOM + localStorage synchronously; backend sync is fire-and-
   * forget so the UI never feels laggy.
   */
  const setTheme = useCallback((nextTheme) => {
    if (!isValidTheme(nextTheme)) return;
    setThemeState(nextTheme);
    applyThemeToDocument(nextTheme);
    writeStoredTheme(nextTheme);
    // Persist for the logged-in user — but only if we actually have one.
    // We deliberately do NOT block on the response: a slow network must
    // not delay the visual switch.
    if (user?.user_id) {
      api.patch("/auth/preferences", { theme_preference: nextTheme }).catch(() => {
        // Swallow: localStorage still holds the value, so on next load
        // (or on next /auth/me which echoes the server value) we will
        // reconcile correctly.
      });
    }
  }, [user?.user_id]);

  /**
   * Reconcile with the server-stored preference exactly once after the
   * user finishes loading. After that, the local state is the source
   * of truth until the user toggles again.
   */
  useEffect(() => {
    if (!user) {
      // Logged out: just keep whatever is currently active. Clear the
      // "synced" flag so the next login triggers a fresh reconciliation.
      hasSyncedUserPrefRef.current = false;
      return;
    }
    if (hasSyncedUserPrefRef.current) return;
    hasSyncedUserPrefRef.current = true;

    const serverPref = (user.theme_preference || "").toLowerCase();
    const localPref = readStoredTheme();

    if (isValidTheme(serverPref)) {
      // Server has an explicit pref → that wins for cross-device
      // consistency.
      if (serverPref !== theme) {
        setThemeState(serverPref);
        applyThemeToDocument(serverPref);
        writeStoredTheme(serverPref);
      }
    } else if (localPref) {
      // Server has no pref yet but the user already picked one on this
      // device → push it to the server so the next device sees it.
      api.patch("/auth/preferences", { theme_preference: localPref }).catch(() => {
        /* fire-and-forget */
      });
    }
    // If neither side has a stored pref, the default (dark) is already
    // applied. Nothing more to do.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  const value = useMemo(() => ({
    theme,
    setTheme,
    /**
     * Hard-set a theme that bypasses the user pref / localStorage. Used
     * by the Owner Console which is locked to dark regardless of the
     * user's preference.
     */
    forceTheme: (t) => applyThemeToDocument(isValidTheme(t) ? t : DEFAULT_THEME),
    /**
     * Restore whatever the user-context theme should be (used by
     * Owner Console on unmount to undo `forceTheme`).
     */
    restoreUserTheme: () => applyThemeToDocument(theme),
  }), [theme, setTheme]);

  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme() {
  const v = useContext(ThemeCtx);
  if (!v) throw new Error("useTheme must be used within ThemeProvider");
  return v;
}
